import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { FieldEvidenceProvider } from '../../../components/evidence/FieldEvidenceContext';
import { EvidenceNagLine } from '../../../components/evidence/EvidenceNagLine';
import { useTheme, alpha } from '@mui/material/styles';
import { Science, Assignment, BarChart, PhotoLibrary } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb } from '../../../components/layout/Breadcrumb';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { TestNotFound } from '../../../components/shared/TestNotFound';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { useRegisterUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { getRockUcsData, saveRockUcsData } from '../../../services/rockUcsService';
import { downloadReport } from '../../../services/reportService';
import type {
  RockUCSConformityReading,
  RockUCSContext,
  RockUCSGeneral,
  RockUCSNavigation,
  RockUCSStageMeta,
  RockUCSUltrasonicReading,
} from '../../../types/rockUcs';
import {
  EMPTY_ROCK_UCS_GENERAL,
  emptyConformityReadings,
  emptyUltrasonicReadings,
  CONFORMITY_POSITIONS_MM,
} from './rockUcsFields';
import { TestFooterActions } from '../components/TestFooterActions';
import { TestActionBar } from '../components/TestActionBar';
import { useReportPreview } from '../../../components/reportPreview/useReportPreview';
import { RockUCSParametersTab } from './RockUCSParametersTab';
import { RockUCSResultEntryTab } from './RockUCSResultEntryTab';
import { RockUCSAnalysisTab } from './RockUCSAnalysisTab';
import { TriaxialPhotosTab } from '../photos/TriaxialPhotosTab';
import { PHOTO_TALL_CORE_ASPECT, ROCK_REPORT_ASPECT_PRESETS } from '../../../types/photo';

const TAB_DEFS = [
  { label: 'Test Parameters', icon: <Science sx={{ fontSize: 18 }} /> },
  { label: 'Result Entry', icon: <Assignment sx={{ fontSize: 18 }} /> },
  { label: 'Analysis & Plots', icon: <BarChart sx={{ fontSize: 18 }} /> },
  { label: 'Photos', icon: <PhotoLibrary sx={{ fontSize: 18 }} /> },
];

const EMPTY_CONTEXT: RockUCSContext = {
  locationName: '', sampleReference: '', depth: 0, testName: '', workOrderId: '',
  projectId: '', projectName: '', clientName: '', siteName: '', sampleType: '', siteKey: '',
  labLocation: 'Fredericton', testedBy: null, testDate: null, reviewedBy: null,
  reviewDate: null, approvedBy: null, approvalDate: null, reportUnit: 'metric', testStatus: '',
};

const EMPTY_NAV: RockUCSNavigation = {
  previousWorkOrderDetailKey: null, nextWorkOrderDetailKey: null, currentIndex: 0, totalSamples: 0,
};

export const RockUCSWorkflowPage = () => {
  const { projectId, workOrderId, workOrderDetailKey } = useParams<{
    projectId: string; workOrderId: string; workOrderDetailKey: string;
  }>();
  const navigate = useNavigate();
  const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { canEditTestData, canSaveTestData } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTabState] = useState(() => {
    const t = parseInt(searchParams.get('tab') || '0', 10);
    return t >= 0 && t < TAB_DEFS.length ? t : 0;
  });
  const setActiveTab = useCallback((tab: number) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 0) next.delete('tab');
      else next.set('tab', String(tab));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [general, setGeneral] = useState<RockUCSGeneral>(EMPTY_ROCK_UCS_GENERAL);
  const [conformity, setConformity] = useState<RockUCSConformityReading[]>(emptyConformityReadings('HQ'));
  const [ultrasonic, setUltrasonic] = useState<RockUCSUltrasonicReading[]>(emptyUltrasonicReadings());
  const [stageMeta, setStageMeta] = useState<RockUCSStageMeta[]>([]);
  const [readingCount, setReadingCount] = useState(0);
  const [context, setContext] = useState<RockUCSContext>(EMPTY_CONTEXT);
  const [navigation, setNavigation] = useState<RockUCSNavigation>(EMPTY_NAV);
  const [testStatus, setTestStatus] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportLabel, setReportLabel] = useState('');
  // Mirrors the FieldEvidenceProvider's missing-required count (Parameters tab badge).
  const [missingEvidence, setMissingEvidence] = useState(0);

  const applyData = useCallback((data: Awaited<ReturnType<typeof getRockUcsData>>) => {
    const coreSize = (data.general?.coreSize as 'HQ' | 'NQ') ?? 'HQ';
    setGeneral(data.general ? { ...EMPTY_ROCK_UCS_GENERAL, ...data.general } : EMPTY_ROCK_UCS_GENERAL);
    setConformity(data.conformityReadings.length > 0 ? data.conformityReadings : emptyConformityReadings(coreSize));
    setUltrasonic(data.ultrasonicReadings.length > 0 ? data.ultrasonicReadings : emptyUltrasonicReadings());
    setStageMeta(data.stageMeta);
    setReadingCount(data.readingCount);
    setContext(data.context);
    setNavigation(data.navigation);
    setTestStatus(data.context.testStatus || '');
    setDirty(false);
  }, []);

  const loadData = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setLoading(true);
    try {
      applyData(await getRockUcsData(workOrderDetailKey));
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) setNotFound(true);
      else setSnackbar({ open: true, message: 'Failed to load test data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey, applyData]);

  useEffect(() => { loadData(); }, [loadData]);

  // BUG-2026-0189 — warn on browser refresh / close / back while there are
  // unsaved parameter edits (this page previously had no such guard).
  useUnsavedChangesWarning(dirty);

  const onGeneralChange = useCallback((field: keyof RockUCSGeneral, value: RockUCSGeneral[keyof RockUCSGeneral]) => {
    setGeneral((prev) => {
      const next = { ...prev, [field]: value } as RockUCSGeneral;
      // Core-size switch re-maps the conformity position rows (values kept by index)
      if (field === 'coreSize' && (value === 'HQ' || value === 'NQ') && value !== prev.coreSize) {
        const positions = CONFORMITY_POSITIONS_MM[value];
        setConformity((rows) => rows.map((r) => ({ ...r, positionMm: positions[r.sortOrder] ?? r.positionMm })));
      }
      return next;
    });
    setDirty(true);
  }, []);

  const onConformityChange = useCallback((next: RockUCSConformityReading[]) => {
    setConformity(next);
    setDirty(true);
  }, []);

  const onUltrasonicChange = useCallback((next: RockUCSUltrasonicReading[]) => {
    setUltrasonic(next);
    setDirty(true);
  }, []);

  const onNotify = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const doSave = useCallback(async (confirmReset?: boolean): Promise<boolean> => {
    if (!workOrderDetailKey) return false;
    setSaving(true);
    try {
      const data = await saveRockUcsData(workOrderDetailKey, general, conformity, ultrasonic, confirmReset);
      applyData(data);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
      return true;
    } catch (err) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
      if (resp?.status === 409 && resp.data?.error === 'TEST_REQUIRES_RESET') {
        setConfirmDlg({
          open: true,
          title: 'Reset Required',
          message: `${resp.data.message ?? 'This test is in a protected status.'} Reset and save?`,
          confirmLabel: 'Reset & Save',
          confirmColor: 'warning',
          onConfirm: () => { void doSave(true); },
        });
      } else {
        setSnackbar({ open: true, message: resp?.data?.message || 'Save failed', severity: 'error' });
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [workOrderDetailKey, general, conformity, ultrasonic, applyData]);

  const handleSave = () => { void doSave(); };

  const autoSaveIfDirty = async (): Promise<boolean> => {
    if (!dirty || !workOrderDetailKey) return true;
    return doSave();
  };

  // BUG-2026-0189 — register the shared unsaved-changes guard so EVERY in-app
  // exit path (top nav, logo, Directory, breadcrumbs, browser Back, and the
  // "Back to Work Order" button below) prompts Save & Leave / Discard & Leave /
  // Cancel while there are unsaved parameter edits.
  const requestLeave = useRegisterUnsavedGuard({ dirty, save: () => doSave(), setConfirm: setConfirmDlg });

  // BUG-2026-0189 — auto-save the current tab's data when switching tabs.
  // Fire-and-forget: the switch is never blocked; a rejected save (e.g. a
  // protected-status 409) keeps the edits in memory + dirty, and the exit guard
  // catches them on the way out.
  const handleTabChange = (idx: number) => {
    void autoSaveIfDirty();
    setActiveTab(idx);
  };

  const handleNextSample = async () => {
    if (!navigation.nextWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.nextWorkOrderDetailKey));
  };

  const handlePreviousSample = async () => {
    if (!navigation.previousWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.previousWorkOrderDetailKey));
  };

  const handleCancel = () => {
    const goBack = () => {
      if (projectId && workOrderId) {
        navigate(ROUTES.WORK_ORDER_DETAIL
          .replace(':projectId', projectId)
          .replace(':workOrderId', workOrderId));
      }
    };
    // BUG-2026-0189 — reuse the shared guard dialog (Save & Leave / Discard &
    // Leave / Cancel) so the button matches every other exit path.
    requestLeave(goBack);
  };

  const handleGenerateReport = async () => {
    if (!workOrderDetailKey) return;
    setReportGenerating(true);
    setReportProgress(0);
    setReportLabel('Starting report generation...');
    const steps = [
      { pct: 10, label: 'Collecting test data...', delay: 300 },
      { pct: 30, label: 'Loading readings...', delay: 1000 },
      { pct: 50, label: 'Building report template...', delay: 2500 },
      { pct: 70, label: 'Rendering charts...', delay: 4500 },
      { pct: 90, label: 'Generating PDF...', delay: 7000 },
    ];
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = [...steps].reverse().find((s) => elapsed >= s.delay);
      if (step) { setReportProgress(step.pct); setReportLabel(step.label); }
    }, 200);
    try {
      await downloadReport(workOrderDetailKey);
      setReportProgress(100);
      setReportLabel('Download complete');
      setSnackbar({ open: true, message: 'Report downloaded', severity: 'success' });
      setTimeout(() => { setReportProgress(0); setReportLabel(''); }, 2000);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Report generation failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      clearInterval(timer);
      setReportGenerating(false);
    }
  };

  const breadcrumbs = [
    { label: context.clientName || '...', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: context.projectName || context.projectId || '...', path: ROUTES.PROJECTS, entityType: 'project' as const },
    { label: context.siteName || '...',
      path: context.siteKey ? ROUTES.SITE_DETAIL.replace(':siteKey', context.siteKey) : undefined,
      entityType: 'site' as const },
    { label: context.locationName || '...',
      path: projectId ? ROUTES.LOCATIONS.replace(':projectId', projectId) : undefined,
      entityType: 'location' as const },
    { label: context.workOrderId || '...',
      path: projectId && workOrderId && !workOrderId.startsWith(':')
        ? ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
        : undefined,
      entityType: 'workOrder' as const },
    { label: context.sampleReference || '...', entityType: 'sample' as const },
    { label: 'Rock UCSM', entityType: 'test' as const },
  ];

  if (notFound) return <TestNotFound entity="test" />;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const page = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Header row: breadcrumb + depth + status | actions ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Breadcrumb items={breadcrumbs} />
          {context.depth != null && context.depth !== 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Depth: {context.depth} m
            </Typography>
          )}
          {testStatus && (
            <Chip
              label={testStatus}
              size="small"
              color={
                testStatus === 'Approved' ? 'success'
                : testStatus === 'Review Completed' ? 'info'
                : testStatus === 'In Progress' ? 'warning'
                : 'default'
              }
              variant="filled"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )}
          {/* Passive evidence nag — a marker, never a popup or save blocker */}
          <EvidenceNagLine />
        </Box>
        {previewDialog}
        <TestActionBar
          leadingStatus={reportGenerating && reportLabel ? (
            <Typography variant="body2" className="print-hide" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
              {reportLabel} ({reportProgress}%)
            </Typography>
          ) : undefined}
          canSave={canSaveTestData}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onPrint={() => {
            if (activeTab !== 0) setActiveTab(0);
            setTimeout(() => window.print(), 60);
          }}
          canPrint={!reportGenerating}
          onGenerateReport={handleGenerateReport}
          onPreviewReport={openPreview}
          onReportHistory={openHistory}
          reportGenerating={reportGenerating}
          pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
        />
      </Box>
      {reportGenerating && (
        <LinearProgress variant="determinate" value={reportProgress} className="print-hide" sx={{ height: 4, mb: 0.5, borderRadius: 2 }} />
      )}

      {/* ── Tab bar (matches DS/Triaxial) ── */}
      <Box className="print-hide" sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', mb: 1.5 }}>
        {TAB_DEFS.map((tab, idx) => {
          const isActive = activeTab === idx;
          return (
            <Box
              key={tab.label}
              onClick={() => handleTabChange(idx)}
              sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                backgroundColor: isActive ? theme.palette.background.paper : '#F0F0EA',
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isActive ? 700 : 400,
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.06),
                },
                '& .MuiSvgIcon-root': {
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  fontSize: 18,
                },
              }}
            >
              {tab.icon}
              {/* Parameters tab carries the missing-evidence count */}
              <Badge badgeContent={idx === 0 ? missingEvidence : 0} color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15, right: -8, top: 2 } }}>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 'inherit' }}>
                  {tab.label}
                </Typography>
              </Badge>
            </Box>
          );
        })}
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <RockUCSParametersTab
            general={general}
            conformity={conformity}
            ultrasonic={ultrasonic}
            onChange={onGeneralChange}
            onConformityChange={onConformityChange}
            onUltrasonicChange={onUltrasonicChange}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 1 && workOrderDetailKey && (
          <RockUCSResultEntryTab
            workOrderDetailKey={workOrderDetailKey}
            general={general}
            stageMeta={stageMeta}
            readingCount={readingCount}
            onChange={onGeneralChange}
            onReloadRequested={loadData}
            onSave={handleSave}
            onNotify={onNotify}
            hasUnsavedParameters={dirty}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 2 && workOrderDetailKey && (
          <RockUCSAnalysisTab
            workOrderDetailKey={workOrderDetailKey}
            general={general}
            onChange={onGeneralChange}
            onSave={handleSave}
            onNotify={onNotify}
            readOnly={!canEditTestData}
          />
        )}
        {/* BUG-2026-0137 §12: Before + After only (no close-up slot);
            §11: tall report crop is the recrop default. */}
        {activeTab === 3 && workOrderDetailKey && (
          <TriaxialPhotosTab
            workOrderDetailKey={workOrderDetailKey}
            categories={['before', 'after']}
            cropAspect={PHOTO_TALL_CORE_ASPECT}
            cropPresets={ROCK_REPORT_ASPECT_PRESETS}
            maxPerCategory={1}
          />
        )}
      </Box>

      {/* ── Sticky bottom action bar (Parameters tab only) ── */}
      {activeTab === 0 && (
        <Box className="print-hide">
          <TestFooterActions
            onSave={handleSave}
            onNextSample={handleNextSample}
            onPreviousSample={handlePreviousSample}
            onCancel={handleCancel}
            saving={saving}
            dirty={dirty}
            hasNext={!!navigation.nextWorkOrderDetailKey}
            hasPrevious={!!navigation.previousWorkOrderDetailKey}
            hideSave={!canSaveTestData}
            pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
          />
        </Box>
      )}

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  // Field-level evidence photos (mockups 2026-07-24): the provider feeds every
  // FieldEvidenceButton in the sections, the header nag line, and the
  // Parameters tab badge.
  return workOrderDetailKey ? (
    <FieldEvidenceProvider
      workOrderDetailKey={workOrderDetailKey}
      testTypeId="rockUcs"
      readOnly={!canEditTestData}
      onMissingCountChange={setMissingEvidence}
    >
      {page}
    </FieldEvidenceProvider>
  ) : page;
};
